// BuyBack UI Component
import React from 'react'
import { BuyBackItem } from '../coinLiquidation/types'
import useBuyBack from '../coinLiquidation/useBuyBack'
import { WalletType } from '../types/wallet'

interface BuyBackSectionProps {
  walletAddress: string
  walletType: WalletType
}

const BuyBackSection: React.FC<BuyBackSectionProps> = ({ walletAddress, walletType }) => {
  const {
    buybackHistory,
    pendingItems,
    totalPendingUSD,
    isLoading,
    isBuyingBack,
    buyBackItem,
    buyBackAll,
    removeItem,
    clearHistory,
    formatUSDAmount: formatUSD,
    itemStatuses,
  } = useBuyBack(walletAddress, walletType)

  const [expandedItem, setExpandedItem] = React.useState<string | null>(null)

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString()
  }

  const handleBuyBack = async (item: BuyBackItem) => {
    try {
      await buyBackItem(item)
      setExpandedItem(null)
    } catch (error: any) {
      alert(`Buyback failed: ${error.message || error}`)
    }
  }

  const handleBuyBackAll = async () => {
    if (pendingItems.length === 0) return

    if (!window.confirm(`Are you sure you want to buyback all ${pendingItems.length} items (${formatUSD(totalPendingUSD)})?`)) {
      return
    }

    try {
      await buyBackAll()
    } catch (error: any) {
      alert(`Buyback failed: ${error.message || error}`)
    }
  }

  const getExplorerBase = (chainId: number) => {
    if (chainId === 101) return 'https://solscan.io'
    if (chainId === 1) return 'https://etherscan.io'
    if (chainId === 56) return 'https://bscscan.com'
    if (chainId === 784) return 'https://suiexplorer.com/txblock'
    return '#'
  }

  const getExplorerLink = (chainId: number, txHash: string) => {
    const base = getExplorerBase(chainId)
    if (chainId === 784) return `${base}/${txHash}`
    return `${base}/tx/${txHash}`
  }

  if (buybackHistory.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-4 sm:p-5 md:p-6">
        <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">BuyBack History</h2>
        <p className="text-sm text-gray-400 text-center py-6 sm:py-8 px-2">
          No buyback history yet. Buyback items will appear here after you sell tokens.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4 sm:p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold">BuyBack History</h2>
          <p className="text-xs sm:text-sm text-gray-400 mt-1">
            {pendingItems.length} pending • {formatUSD(totalPendingUSD)} total
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
          {pendingItems.length > 0 && (
            <button
              onClick={handleBuyBackAll}
              disabled={isBuyingBack || isLoading}
              className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-600 rounded-lg text-xs sm:text-sm font-medium min-h-[44px] sm:min-h-[40px] transition-colors"
            >
              {isBuyingBack ? (
                <span className="hidden sm:inline">Buying Back...</span>
              ) : (
                <>
                  <span className="hidden sm:inline">BuyBack All ({pendingItems.length})</span>
                  <span className="sm:hidden">BuyBack All ({pendingItems.length})</span>
                </>
              )}
            </button>
          )}
          <button
            onClick={() => {
              if (window.confirm('Clear all buyback history?')) {
                clearHistory()
              }
            }}
            className="w-full sm:w-auto px-4 py-2 bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-lg text-xs sm:text-sm font-medium flex items-center justify-center gap-2 min-h-[44px] sm:min-h-[40px] transition-colors"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span>Clear All</span>
          </button>
        </div>
      </div>

      <div className="space-y-2 sm:space-y-3">
        {buybackHistory
          .sort((a, b) => b.timestamp - a.timestamp)
          .map((item) => {
            const isExpanded = expandedItem === item.id
            const status = itemStatuses[item.id] || 'pending'
            const isBuyingBackThis = status === 'processing'

            return (
              <div
                key={item.id}
                className={`border rounded-lg p-3 sm:p-4 transition-colors ${
                  status === 'completed'
                    ? 'border-green-600 bg-green-900/10'
                    : status === 'failed'
                    ? 'border-red-600 bg-red-900/10'
                    : 'border-gray-600 bg-gray-700/50'
                }`}
              >
                <div
                  className="flex items-center justify-between cursor-pointer min-h-[48px] sm:min-h-[44px]"
                  onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                >
                  <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                    {status !== 'pending' && (
                      <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0 ${
                        status === 'completed'
                          ? 'bg-green-500'
                          : status === 'failed'
                          ? 'bg-red-500'
                          : status === 'processing'
                          ? 'bg-blue-500 animate-pulse'
                          : ''
                      }`}></div>
                    )}
                    {status === 'pending' && <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0"></div>}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-white text-sm  truncate">
                        {item.inputToken.symbol} → {item.outputToken.symbol}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-400 truncate">
                        {item.inputToken.networkName} • {formatDate(item.timestamp)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-2 flex-shrink-0">
                    <div className="font-medium text-white text-sm  whitespace-nowrap">
                      {formatUSD(item.buybackAmountUSD)}
                    </div>
                    {status !== 'pending' && (
                      <div className="text-xs text-gray-400 capitalize">
                        {status}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 sm:gap-2 ml-2 sm:ml-4 flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        if (window.confirm('Remove this item from history?')) {
                          removeItem(item.id)
                        }
                      }}
                      className="p-1.5 sm:p-1 text-gray-400 hover:text-red-400 active:text-red-500 transition-colors min-w-[44px] sm:min-w-[40px] min-h-[44px] sm:min-h-[40px] flex items-center justify-center"
                      title="Remove item"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <svg
                      className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform flex-shrink-0 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-600 space-y-3 sm:space-y-3">
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                      <div>
                        <div className="text-gray-400 mb-1">Sold</div>
                        <div className="text-white font-medium text-xs sm:text-sm break-words">
                          {Number(item.soldAmount).toFixed(6)} {item.inputToken.symbol}
                        </div>
                        <div className="text-gray-400 text-xs">
                          {formatUSD(item.soldAmountUSD)}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 mb-1">Received</div>
                        <div className="text-white font-medium text-xs sm:text-sm break-words">
                          {Number(item.receivedAmount).toFixed(6)} {item.outputToken.symbol}
                        </div>
                        <div className="text-gray-400 text-xs">
                          {formatUSD(item.receivedAmountUSD)}
                        </div>
                      </div>
                    </div>

                    <div className="pt-2">
                      <div className="text-gray-400 text-xs sm:text-sm mb-1">BuyBack Amount</div>
                      <div className="text-white font-semibold text-base sm:text-lg break-words">
                        {formatUSD(item.buybackAmountUSD)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Will buy back {item.inputToken.symbol} using {item.outputToken.symbol}
                      </div>
                    </div>

                    {item.txHash && (
                      <div className="pt-2">
                        <div className="text-gray-400 text-xs sm:text-sm mb-1">Transaction</div>
                        <a
                          href={getExplorerLink(item.inputToken.chainId, item.txHash)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm font-mono break-all"
                        >
                          {item.txHash.slice(0, 10)}...{item.txHash.slice(-8)}
                        </a>
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      {(status === 'pending' || status === 'failed') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleBuyBack(item)
                          }}
                          disabled={isBuyingBackThis || isLoading}
                          className="flex-1 px-4 py-2.5 sm:py-2 bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-600 rounded-lg text-xs sm:text-sm font-medium min-h-[48px] sm:min-h-[44px] transition-colors"
                        >
                          {isBuyingBackThis ? 'Processing...' : 'BuyBack Now'}
                        </button>
                      )}
                      {status === 'processing' && (
                        <div className="flex-1 px-4 py-2.5 sm:py-2 bg-blue-600 rounded-lg text-xs sm:text-sm font-medium text-center min-h-[48px] sm:min-h-[44px] flex items-center justify-center">
                          Processing...
                        </div>
                      )}
                      {status === 'completed' && (
                        <div className="flex-1 px-4 py-2.5 sm:py-2 bg-green-600 rounded-lg text-xs sm:text-sm font-medium text-center min-h-[48px] sm:min-h-[44px] flex items-center justify-center">
                          Completed
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default BuyBackSection
